import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import * as moment from 'moment/moment';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, of } from 'rxjs';
import { catchError, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { LocationService } from 'src/app/location/services/location.service';
import { SettingsService } from 'src/app/settings/services/settings.service';
import { ServiceDestroyed } from '../../core/decorators/take-until-destroy';
import { List } from '../../core/models/list';
import { Filters } from '../../core/services/list/models/Filters';
import { Order } from '../../core/services/list/models/Order';
import { Paginator } from '../../core/services/list/models/Paginator';
import { TextFilterField } from '../../core/services/list/models/TextFilterField';
import { SortDirection } from '../../core/services/list/models/sort-direction.enum';
import { SurveyLogService } from '../../core/services/survey-log.service';
import { Warehouse } from '../../orders/models/warehouse.model';
import { ConfirmService } from '../../utils/confirm/confirm.service';
import { MassactionService } from '../../utils/massaction/massaction.service';
import { MassActionPayload } from '../../utils/massaction/models/mass-action-payload.model';
import { AddProductSeriesModalComponent } from '../../utils/modals/add-product-series-modal/add-product-series-modal.component';
import { AddProductSeriesModel } from '../models/add-product-series.model';
import { CreateProduct } from '../models/create-product.model';
import { DefaultLocation } from '../models/defaultLocation.model';
import { Inventory } from '../models/inventory.model';
import { MaximumQuantity } from '../models/maximum-quantity.model';
import { NewRepLevel } from '../models/new-replenishment-level.model';
import { ProductSeries } from '../models/product-series.model';
import { Product } from '../models/product.model';
import { RelatedOrder } from '../models/related-order.model';
import { UpdateProduct } from '../models/update-product.model';
import { PimApiService } from './pim-api.service';

@Injectable()
export class PimService implements ServiceDestroyed {
  serviceDestroyed = new Subject<void>();

  product: Product = null;

  fallbackLocation: number;

  productLocations: Inventory[];

  defaultLocations: DefaultLocation[] = [];

  productsReservedSeries: Inventory[] = [];

  productWarehouses: Warehouse[] = [];

  productList: Product[] = null;

  maxQtyLocationList: MaximumQuantity;

  constructor(
    private pimApiService: PimApiService,
    private confirmService: ConfirmService,
    private surveyLogService: SurveyLogService,
    private toastr: ToastrService,
    private router: Router,
    private dialog: MatDialog,
    private massactionService: MassactionService,
    private toastrService: ToastrService,
    private settingsService: SettingsService,
    private locationService: LocationService,
  ) {}

  getReservedVariants(productId: number): void {
    this.pimApiService
      .getProductLocations(productId)
      .pipe(
        map(({ items }) => items.filter(item => item.reserved > 0)),
        tap(items => (this.productsReservedSeries = items)),
        takeUntil(this.serviceDestroyed),
        catchError(err => of(err)),
      )
      .subscribe();
  }

  mapInventoryToProduct(product: Product) {
    return this.pimApiService.getProductLocations(product?.id).pipe(
      map(({ items }) => {
        return items.map(({ quantity, location: { barcode }, product: { id, expiresAt, ean } }) => ({
          id,
          ean,
          barcode,
          expiresAt,
          quantity,
        }));
      }),
      tap(items => {
        const barcodes = [...new Set(items.map(item => item.barcode))];
        const mapItems: ProductSeries[] = barcodes.map(barcode => ({ barcode, series: [] }));
        items.forEach(({ barcode, expiresAt, quantity }) => {
          mapItems.forEach(mapItem => {
            if (mapItem.barcode === barcode) {
              mapItem.series.push({ expiresAt, quantity });
            }
          });
        });
        product.inventory = mapItems;
      }),
      catchError(err => of(err)),
    );
  }

  getProductWarehouses(productId: number): Observable<List<Warehouse>> {
    return this.pimApiService.getProductWarehouses(productId).pipe(
      tap(({ items }) => (this.productWarehouses = items)),
      takeUntil(this.serviceDestroyed),
      catchError(err => of(err)),
    );
  }

  addWarehouseToProduct(productId: number, warehouses: number[]): Observable<List<Warehouse>> {
    return this.pimApiService.addWarehouseToProduct(productId, warehouses).pipe(
      tap(() => this.toastr.success('Product has been assigned to warehouse!')),
      switchMap(() => this.getProductWarehouses(productId)),
      takeUntil(this.serviceDestroyed),
      catchError(err => of(err)),
    );
  }

  removeWarehouseFromProduct(productId: number, warehouseId: number): Observable<List<Warehouse>> {
    return this.pimApiService.removeWarehouseFromProduct(productId, [warehouseId]).pipe(
      tap(() => this.toastr.success('Product has been removed from warehouse!')),
      switchMap(() => this.getProductWarehouses(productId)),
      takeUntil(this.serviceDestroyed),
      catchError(err => of(err)),
    );
  }

  addProductSeries(payload: AddProductSeriesModel): Observable<any> {
    return this.pimApiService.addProductSeries(payload);
  }

  checkExistProductSeries(data: AddProductSeriesModel, actionAfterCheck: Observable<any> = of()): Observable<any> {
    const filters = new Filters(
      [new TextFilterField('expiresAt', 'expiresAt').patchValue(data.expiresAt)],
      new Order(['serialNumber', SortDirection.DESC], []),
      new Paginator(1, 9999),
    );

    return this.pimApiService.checkExistProductSeries(filters, data.product).pipe(
      switchMap(({ items: products }) => {
        const seriesExist =
          products.length &&
          products.some(({ serialNumber: productSerialNumber }) => productSerialNumber === data.serialNumber);

        if (products.length === 0) {
          return this.addProductSeries(data).pipe(switchMap(() => actionAfterCheck));
        }

        if (products.length > 0 && !seriesExist && data?.serialNumber && data?.expiresAt) {
          const question = `A product with an expiration date already exists and has a different LOT number. Is the specified LOT ${data.serialNumber} correct?`;
          return this.confirmService.show(question).pipe(
            switchMap(decision =>
              this.surveyLogService
                .createSurveyLog(question, decision ? 'yes' : 'no', data.product)
                .pipe(switchMap(() => of(decision))),
            ),
            filter(decision => decision),
            switchMap(() => this.addProductSeries(data)),
            switchMap(() => actionAfterCheck),
            catchError(err => of(err)),
          );
        } else {
          return actionAfterCheck;
        }
      }),
    );
  }

  getProducts(filters: Filters): Observable<List<Product>> {
    return this.pimApiService.getProducts(filters);
  }

  deleteProduct(productId: number): Observable<void> {
    return this.confirmService.show('Do you really want to delete this product?').pipe(
      filter(decision => decision === true),
      switchMap(() =>
        this.pimApiService.removeProduct(productId).pipe(
          tap(() => {
            this.toastr.success('Product has been deleted!');
            this.router.navigateByUrl('/pim');
          }),
        ),
      ),
    );
  }

  getProduct(id: number): Observable<Product> {
    return this.pimApiService.getProduct(id).pipe(tap(product => (this.product = product)));
  }

  mapLocationMaximumQuantity(
    items: Inventory[] | DefaultLocation[],
    productId: number,
    defaultLocation = false,
  ): Inventory[] | DefaultLocation[] {
    return items.map(item => ({
      ...item,
      location: {
        ...item.location,
        maxFillPercentage:
          item.capacities?.items?.find(capacity => capacity.product === productId)?.maxFillPercentage ??
          (defaultLocation ? 0 : null),
        maximumQuantity:
          item.capacities?.items?.find(capacity => capacity.product === productId)?.capacity ??
          (defaultLocation ? 0 : null),
      },
    }));
  }

  getProductLocations(productId: number): Observable<any> {
    return this.pimApiService.getProductLocations(productId).pipe(
      map(({ items }) => this.mapLocationMaximumQuantity(items, productId)),
      tap(items => (this.productLocations = items)),
    );
  }

  getProductDefaultLocations(productId: number): Observable<any> {
    return this.pimApiService.getProductsDefaultLocations(productId).pipe(
      map(({ items }) => this.mapLocationMaximumQuantity(items, productId, true)),
      tap(items => (this.defaultLocations = items)),
    );
  }

  removeDefaultLocation(locationId: number): Observable<List<DefaultLocation>> {
    return this.pimApiService
      .removeDefaultLocation(this.product?.id, locationId)
      .pipe(switchMap(() => this.getProductDefaultLocations(this.product?.id)));
  }

  setMaxLocationQty(locationArray, maxQtyArray) {
    if (locationArray.length > 0 && maxQtyArray.length > 0) {
      locationArray.forEach(item => {
        maxQtyArray.forEach(maxQtyItem => {
          if (item.location?.id === maxQtyItem.location?.id) {
            item.location.maximumQuantity = maxQtyItem.capacity;
          } else if (item?.id === maxQtyItem.location?.id) {
            item.maximumQuantity = maxQtyItem.capacity;
          } else if (!item.maximumQuantity) {
            item.maximumQuantity = 0;
          }
        });
      });
    } else {
      locationArray.forEach(item => (item.maximumQuantity = 0));
    }
  }

  addDefaultLocation(locationId: number): Observable<void> {
    return this.pimApiService.addDefaultLocation(this.product?.id, locationId);
  }

  updateProduct(payload: UpdateProduct, cleanUpdate: boolean = false): Observable<void> {
    return this.pimApiService.updateProduct(payload, this.product?.id).pipe(
      tap(() => this.toastr.success('Product has been updated')),
      tap(() => cleanUpdate && this.putQuantityPerLocation()),
      switchMap(() => this.getLocationsData(this.product?.id)),
    );
  }

  putQuantityPerLocation(): void {
    this.defaultLocations.forEach(item => {
      if (item.location.maxFillPercentage > 0) {
        this.pimApiService
          .putQuantityPerLocation(this.product?.id, item.location.sizeId, item.location.maxFillPercentage)
          .pipe(switchMap(() => this.getProductDefaultLocations(this.product?.id)))
          .subscribe();
      }
    });
  }

  addSeriesToProduct(): Observable<void> {
    const dialogRef = this.dialog.open(AddProductSeriesModalComponent, {
      maxWidth: '500px',
    });

    return dialogRef.componentInstance.onApprove.pipe(
      switchMap(({ expiresAt, producedAt, serialNumber }) =>
        this.pimApiService.addProductSeries({
          serialNumber,
          expiresAt: expiresAt ? moment(expiresAt).format('YYYY-MM-DD') : null,
          producedAt: producedAt ? moment(producedAt).format('YYYY-MM-DD') : null,
          product: this.product.id,
        }),
      ),
      tap(() => this.toastr.success('Series has been added!')),
    );
  }

  getProductRelatedOrders(): Observable<List<RelatedOrder>> {
    return this.pimApiService.getProductRelatedItems(this.product.id);
  }

  changeShortDateLevel(payload: MassActionPayload, shortDateLevel: number): Observable<NewRepLevel> {
    const { filters: filtersInstate, items } = payload;
    if (filtersInstate.state) {
      const { filters, itemsCount: productCount } = filtersInstate;
      const { paginator } = filters;
      filters.paginator = new Paginator(1, 999999);
      return this.pimApiService.shortDateLevelByFilters({ shortDateLvlId: shortDateLevel, productCount }, filters).pipe(
        tap(() => {
          this.massactionService.resetMassAction();
          this.toastrService.success('Replenishment level has been changed');
          filters.paginator = paginator;
        }),
      );
    } else if (items.length > 0) {
      return this.pimApiService
        .changeShortDateLevel({
          productIds: items,
          shortDateLvlId: shortDateLevel,
        })
        .pipe(
          tap(() => {
            this.massactionService.resetItems();
            this.toastrService.success('Short date level has been changed');
          }),
        );
    }
  }

  createProduct(payload: CreateProduct): Observable<Product> {
    return this.pimApiService
      .createProduct(payload)
      .pipe(tap(() => this.toastrService.success('Product has been created!')));
  }

  getProductByEan(ean: string): Observable<Product> {
    return this.pimApiService.getProductByEan(ean);
  }

  getLocationsData(productId: number): Observable<void> {
    return this.settingsService.getSettingValue('fallback_location').pipe(
      tap(fallback_location => (this.fallbackLocation = parseInt(fallback_location, 10))),
      switchMap(() => this.locationService.getLocationSizes()),
      switchMap(() => this.getProductLocations(productId)),
      tap(() => {
        this.productLocations.forEach(productLocation => {
          const capacity = productLocation.capacities.items.find(
            capacity => capacity.location.id === productLocation.location.id,
          );
          productLocation.location.sizeId = capacity?.location?.sizeId;
          productLocation.location.locationSize =
            this.locationService.locationSizes.find(
              locationSize => locationSize.id === productLocation.location.sizeId,
            ) ?? null;
        });
      }),
      switchMap(() => this.getProductDefaultLocations(productId)),
      tap(() => {
        this.defaultLocations.forEach(defaultLocation => {
          defaultLocation.location.locationSize =
            this.locationService.locationSizes.find(
              locationSize => locationSize.id === defaultLocation.location.sizeId,
            ) ?? null;
          defaultLocation.location.quantity = this.productLocations.reduce((acc, curr) => {
            if (curr.location.id === defaultLocation.location.id) return acc + curr.quantity;
            return acc;
          }, 0);
        });
      }),
    );
  }
}
